<template>
  <Transition name="overlay" appear>
    <div
      v-if="isVisible"
      class="fixed inset-0 z-[1000] flex flex-col items-center justify-center p-4 connection-error-overlay"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      :aria-describedby="descriptionId"
    >
      <!-- Timer Warning Banner -->
      <div v-if="timerActive" class="timer-warning-banner" role="alert" aria-live="assertive">
        <div class="flex items-center justify-center gap-2 font-semibold text-sm md:text-base">
          <span role="img" :aria-label="STRINGS.errors.connectionOverlay.timerWarning.iconLabel"
            ><AppIcon :name="timerWarningIcon" :size="16" color="white"
          /></span>
          <span>
            {{ STRINGS.errors.connectionOverlay.timerWarning.text }}
            <span v-if="timeRemaining !== undefined" class="font-bold text-white/90">
              {{ STRINGS.templates.timerRemaining(timeRemaining) }}
            </span>
          </span>
        </div>
      </div>

      <!-- Modal Content -->
      <div class="connection-error-modal" @click.stop>
        <!-- Error Icon and Title -->
        <div class="mb-6">
          <div class="error-icon" :class="errorIconClasses" role="img" :aria-label="errorIconLabel">
            <AppIcon :name="errorIcon" :size="48" />
          </div>
          <h2
            :id="titleId"
            class="error-title text-xl font-bold text-arena-text-bright m-0 md:text-2xl"
          >
            {{ errorTitle }}
          </h2>
        </div>

        <!-- Error Description -->
        <div
          :id="descriptionId"
          class="error-description text-base text-arena-text-muted leading-relaxed mb-6"
        >
          {{ errorDescription }}
        </div>

        <!-- Reconnection Progress -->
        <div
          v-if="reconnectionState.isReconnecting"
          class="reconnection-progress mb-6 p-4 bg-[rgba(201,168,76,0.1)] border border-energy-primary rounded-lg"
        >
          <div class="reconnection-status text-center" role="status" aria-live="polite">
            <div class="text-sm text-arena-text-bright mb-3 font-medium">
              {{ STRINGS.errors.connectionOverlay.reconnection.attempting }}
              <span class="text-energy-primary font-semibold">
                {{
                  STRINGS.templates.reconnectionProgress(
                    reconnectionState.attempt,
                    reconnectionState.maxAttempts
                  )
                }}
              </span>
            </div>
            <LoadingDots
              :aria-label="
                STRINGS.templates.reconnectionAriaLabel(
                  reconnectionState.attempt,
                  reconnectionState.maxAttempts
                )
              "
            />
          </div>
        </div>

        <!-- Action Buttons -->
        <div
          class="error-actions flex flex-col gap-3 mb-6 sm:flex-row sm:justify-center sm:gap-4"
          role="group"
          :aria-label="actionsAriaLabel"
        >
          <PrimeButton
            v-if="!reconnectionState.isReconnecting"
            @click="handleRetry"
            :aria-label="retryAriaLabel"
            class="action-button order-1 sm:flex-1 sm:max-w-[140px]"
            data-testid="retry-connection-button"
          >
            {{ retryButtonText }}
          </PrimeButton>
        </div>

        <!-- Additional Info -->
        <div class="border-t border-arena-border pt-4">
          <p class="text-sm text-arena-text-muted m-0 leading-normal">{{ errorHint }}</p>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import PrimeButton from './ui/PrimeButton.vue';
import LoadingDots from './ui/LoadingDots.vue';
import AppIcon from './ui/icons/AppIcon.vue';
import type { IconName } from './ui/icons/icon-paths';
import { useFocusTrapping } from '@/composables/useFocusTrapping';
import { STRINGS } from '@/constants/strings';

interface ErrorConfig {
  icon: IconName;
  title: string;
  description: string;
  hint: string;
  iconClass: string;
  iconLabel: string;
}

// Error types
export type ConnectionErrorType = 'connection_lost' | 'server_timeout' | 'sync_failed' | 'unknown';

// Props interface
interface Props {
  /** Whether the overlay is visible */
  isVisible: boolean;
  /** Type of connection error */
  errorType: ConnectionErrorType;
  /** Current reconnection state */
  reconnectionState: {
    isReconnecting: boolean;
    attempt: number;
    maxAttempts: number;
  };
  /** Whether the game timer is still active during disconnection */
  timerActive?: boolean;
  /** Time remaining in seconds if timer is active */
  timeRemaining?: number;
}

const props = withDefaults(defineProps<Props>(), {
  timerActive: false,
  timeRemaining: undefined,
});

// Emits
const emit = defineEmits<{
  retryConnection: [];
}>();

// Component IDs for accessibility
const titleId = 'connection-error-title';
const descriptionId = 'connection-error-description';

// Focus trapping
const { enable, disable } = useFocusTrapping();

// Error type configurations
const errorConfigs: Record<ConnectionErrorType, ErrorConfig> = {
  connection_lost: STRINGS.errors.connectionOverlay.types.connectionLost,
  server_timeout: STRINGS.errors.connectionOverlay.types.serverTimeout,
  sync_failed: STRINGS.errors.connectionOverlay.types.syncFailed,
  unknown: STRINGS.errors.connectionOverlay.types.unknown,
};

// Timer warning icon (typed at source, not cast in template)
const timerWarningIcon: IconName = STRINGS.errors.connectionOverlay.timerWarning.icon;

// Computed properties
const errorConfig = computed(() => errorConfigs[props.errorType]);
const errorIcon = computed((): IconName => errorConfig.value.icon);
const errorTitle = computed(() => errorConfig.value.title);
const errorDescription = computed(() => errorConfig.value.description);
const errorHint = computed(() => errorConfig.value.hint);
const errorIconClasses = computed(() => ['error-icon', errorConfig.value.iconClass]);
const errorIconLabel = computed(() => errorConfig.value.iconLabel);

const retryButtonText = computed(() => {
  return props.reconnectionState.attempt > 0
    ? STRINGS.ui.buttons.retryConnection
    : STRINGS.ui.buttons.reconnect;
});

// ARIA labels
const actionsAriaLabel = computed(() =>
  STRINGS.templates.connectionErrorActionsAriaLabel(props.errorType)
);
const retryAriaLabel = computed(() => STRINGS.templates.retryConnectionAriaLabel(props.errorType));

// Event handlers
const handleRetry = () => {
  emit('retryConnection');
};

// Focus management
onMounted(async () => {
  if (props.isVisible) {
    await nextTick();
    enable('.connection-error-modal');
  }
});

onUnmounted(() => {
  disable();
});

// Watch for visibility changes to manage focus
import { watch } from 'vue';
watch(
  () => props.isVisible,
  async isVisible => {
    if (isVisible) {
      await nextTick();
      enable('.connection-error-modal');
    } else {
      disable();
    }
  }
);
</script>

<style scoped>
.connection-error-overlay {
  background: rgba(10, 10, 15, 0.94);
  will-change: opacity;
}

.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 300ms ease-out;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

.overlay-enter-to,
.overlay-leave-from {
  opacity: 1;
}

.timer-warning-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, var(--color-energy-warning), #d97706);
  color: white;
  padding: 0.75rem 1rem;
  z-index: 1001;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
  animation: banner-slide-down 300ms ease-out;
  min-height: 0;
  overflow: hidden;
}

@keyframes banner-slide-down {
  from {
    opacity: 0;
    transform: translateY(0);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.connection-error-modal {
  background: var(--color-arena-surface);
  border: 2px solid var(--color-arena-border);
  border-radius: 16px;
  padding: 2rem;
  max-width: 480px;
  width: 100%;
  text-align: center;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.3),
    0 10px 10px -5px rgba(0, 0, 0, 0.2);
  animation: modal-scale-in 300ms ease-out;
  position: relative;
  margin: 1rem;
  margin-top: max(4rem, env(safe-area-inset-top, 1rem));
  margin-bottom: max(1rem, env(safe-area-inset-bottom, 1rem));
  max-height: calc(100vh - 8rem);
  overflow-y: auto;
  will-change: transform, opacity;
  transform-origin: center;
}

@keyframes modal-scale-in {
  0% {
    opacity: 0;
    transform: scale(0.8) translateY(0);
  }
  60% {
    opacity: 0.8;
    transform: scale(1.02) translateY(0);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes modal-scale-out {
  0% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
  100% {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
}

.error-icon {
  margin-bottom: 1rem;
  display: inline-block;
  animation: icon-pulse 2s ease-in-out infinite;
}

.error-icon--warning {
  color: var(--color-energy-warning);
}

.error-icon--timeout {
  color: var(--color-energy-warning);
}

.error-icon--sync {
  color: var(--color-arena-text-muted);
}

.error-icon--error {
  color: var(--color-energy-danger);
}

@keyframes icon-pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
}

.action-button {
  min-height: 48px;
  font-weight: 600;
  transition:
    transform 300ms ease-out,
    background-color 300ms ease-out,
    border-color 300ms ease-out,
    box-shadow 300ms ease-out;
}

@media (min-width: 640px) {
  .connection-error-modal {
    padding: 3rem;
    max-width: 520px;
  }
}

@media (min-width: 768px) {
  .connection-error-modal {
    max-width: 600px;
  }

  .error-icon {
    /* icon size is controlled by :size prop */
  }
}

@media (prefers-contrast: high) {
  .connection-error-overlay {
    background: rgba(0, 0, 0, 0.95);
  }

  .connection-error-modal {
    border-color: #ffffff;
    background: #000000;
  }
}

@media (prefers-reduced-motion: reduce) {
  .overlay-enter-active,
  .overlay-leave-active {
    transition: opacity 150ms ease-out;
  }

  .connection-error-overlay,
  .connection-error-modal,
  .timer-warning-banner,
  .error-icon {
    animation: none;
  }

  .error-icon {
    transform: none;
  }

  .timer-warning-banner {
    transform: none;
  }

  .connection-error-modal {
    transform: none;
  }
}

.connection-error-modal:focus {
  outline: none;
}

@media (max-width: 639px) {
  .action-button {
    min-height: 44px;
    padding: 0.75rem 1rem;
  }

  .connection-error-modal {
    margin: 1rem;
    padding: 1.5rem;
  }
}
</style>
